/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { getInfo } from '../../utils/UNSAFE_visUtils';
import { useChartDrill } from '../PRIVATE_useChartDrill';
import { useSelection } from '../PRIVATE_useVisSelection/useSelection';
import { mergeProps } from '../../utils/UNSAFE_mergeProps';

type DrillProps = Parameters<typeof useChartDrill>[0];

type SelectionProps = {
  selection: (string | number)[];
  onChange?: (detail: { ids: (string | number)[] }) => void;
};

export function useSelectDrill({
  selectionMode,
  selection,
  onChange,
  rootRef,
  drilling,
  onItemDrill,
  onGroupDrill,
  focusedGroupInfo,
  focusedItemInfo,
  groups,
  getDataItem
}: DrillProps & SelectionProps) {
  const idExtracter = (event: Event) => {
    const isItemFocused = focusedItemInfo.isCurrent;
    const info =
      isItemFocused && event.type === 'keyup' ? focusedItemInfo : getInfo(rootRef, event);
    return info && info.seriesIndex != null && info.groupIndex != null
      ? getDataItem(info.seriesIndex, info.groupIndex)?.id
      : undefined;
  };

  const selectionProps = useSelection<string | number>({
    idExtracter,
    isDrillEnabled: drilling === 'on',
    selection,
    selectionMode,
    onChange
  });

  const drillProps = useChartDrill({
    rootRef,
    selectionMode,
    drilling,
    focusedItemInfo,
    focusedGroupInfo,
    groups,
    getDataItem,
    onItemDrill,
    onGroupDrill
  });

  return mergeProps(selectionProps, drillProps);
}
